import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { convertPriceValueAndUnit } from "./InventorySearch/price-helpers";
import { TrashIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  AddressFormat,
  formatAddress,
  sendAuthenticatedRequest,
} from "../common/functions";
import PaymentForm from "./PaymentForm";
import { Product } from "../common/models/product";
import { useReactToPrint } from "react-to-print";
import {
  Cart as ShoppingCart,
  CartCheckoutDefaults,
  CartItem,
  ShipmentOption,
  ShippingSelectionPreference,
  normalizeCart,
  parseStoredCart,
} from "../common/models/cart";
import { OrderDetailsApiPayload } from "../common/models/order";
import { ClipLoader } from "react-spinners";
import { useNotifications, useUser } from "../common/stateDefs";
import { BookmarkedItems } from "./BookmarkedItems";
import { KonoGuaranteeBadge } from "./KonoGuaranteeBadge";
import { AS9120Badge } from "./AS9120Badge";
import {
  getAvailableQuantityUnits,
  resolveQuantityUnit,
  getQuantityError,
  simpleFormatUnit,
} from "./InventorySearch/quantity-helpers";
import { UnitSelect } from "./InventorySearch/InventoryDetailsPrice";
import { FLAG_CART_PROMOTION_CODES } from "../featureFlags";
import { usePostHog } from "posthog-js/react";
import {
  LAST_USED_GUEST_EMAIL_KEY,
  LAST_USED_GUEST_PHONE_KEY,
} from "../common/localStorage";
import { captureAnalyticsEvent, identifyAnalyticsUser } from "../analytics";
import {
  clearStoredPromotionCode,
  getStoredPromotionCode,
  storePromotionCode,
  validatePromotionCode,
} from "../common/promotionCodes";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import QuoteDialog from "../quotes/QuoteDialog";
import { CompanyCartsPanel } from "../cart/CompanyCartsPanel";
import {
  persistShoppingCart,
  reconcileCartWithAccount,
} from "../cart/serverCart";
import { QuoteFlowConfig, chooseQuoteAlternative, loadQuoteFlowConfig } from "../quotes/api";
import {
  BeingRevised,
  QUOTE_REOPENED_EVENT,
  quoteBeingBought,
  reopenInCart,
  quoteBeingRevised,
  stopBuyingQuote,
  stopRevising as forgetRevisedQuote,
} from "../quotes/reopen";

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();

const getCurrencyFormatter = (currency: string) => {
  const normalizedCurrency = (currency || "USD").toUpperCase();
  let formatter = currencyFormatterCache.get(normalizedCurrency);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    currencyFormatterCache.set(normalizedCurrency, formatter);
  }
  return formatter;
};

const formatMoney = (value: number, currency = "USD") =>
  getCurrencyFormatter(currency).format(value || 0);

const getCartItemCurrency = (item: CartItem) =>
  item.product.price?.currency || "USD";

const getCartItemsCurrency = (items: CartItem[]) =>
  items.find((item) => item.product.price?.currency)?.product.price?.currency ||
  "USD";

// How each carrier is billed. Operators set this in Internal > Tools > Shipping
// Configuration and the server sends it back with every quote, so the cart shows
// exactly what checkout will charge (see computeOrderTotals). These defaults are
// the long-standing behaviour, used until the first quote arrives: parcel and the
// manual placeholder give the standard tier away and bill only an upgrade to the
// faster service, while freight has no free tier.
type ShippingBillingPolicy = {
  [vendorId: string]: "never" | "when-upgraded" | "always";
};

const DEFAULT_SHIPPING_BILLING: ShippingBillingPolicy = {
  ups: "when-upgraded",
  wwex: "always",
  "kono-m": "never",
};

let shippingBilling: ShippingBillingPolicy = DEFAULT_SHIPPING_BILLING;

// An option with no free tier: the buyer pays its rate however they got to it,
// which makes it the upgrade rather than the standard choice.
const isAlwaysBilledOption = (option?: ShipmentOption): boolean =>
  !!option && shippingBilling[option.vendorId] === "always";

// Single source of truth for what the buyer is charged for a given shipment
// option + selection. A carrier with no rule falls back to the parcel model,
// matching what the server bills (see computeOrderTotals).
const getShipmentOptionCharge = (
  option: ShipmentOption,
  selectedShippingOption: ShippingSelectionPreference,
): number => {
  const billing = shippingBilling[option.vendorId] || "when-upgraded";
  if (billing === "never") {
    return 0;
  }
  if (billing === "always") {
    return option.totalCharges.monetaryValue || 0;
  }
  return selectedShippingOption === "fastest"
    ? option.totalCharges.monetaryValue || 0
    : 0;
};

function getSelectedShippingOptionPreference(
  item: CartItem,
): ShippingSelectionPreference {
  return item.selectedShippingOption === "fastest" ? "fastest" : "cheapest";
}

function getShippingChargeAmount(item: CartItem): number {
  if (!item.shippingCost?.available) {
    return 0;
  }

  // shippingCost.cost is computed by buildShippingCost via getShipmentOptionCharge,
  // so it already reflects freight-vs-parcel billing for the selected option.
  return item.shippingCost.cost || 0;
}

function getShipmentOptionChargeAmount(option?: ShipmentOption): number {
  return option?.totalCharges.monetaryValue || 0;
}

function buildShippingCost(
  option: ShipmentOption,
  selectedShippingOption: ShippingSelectionPreference,
): CartItem["shippingCost"] {
  return {
    available: true,
    cost: getShipmentOptionCharge(option, selectedShippingOption),
    estimatedDeliveryDate: option.estimatedDeliveryDate?.date,
    carrierKey: option.vendorId,
    quoteNumber: option.vendorShipmentReference,
  };
}

function areSameShippingOptions(
  firstOption: ShipmentOption | undefined,
  secondOption: ShipmentOption | undefined,
): boolean {
  if (!firstOption || !secondOption) {
    return false;
  }

  return (
    firstOption.vendorId === secondOption.vendorId &&
    firstOption.vendorShipmentReference ===
      secondOption.vendorShipmentReference &&
    firstOption.serviceCode === secondOption.serviceCode &&
    firstOption.totalCharges.monetaryValue ===
      secondOption.totalCharges.monetaryValue &&
    firstOption.estimatedDeliveryDate?.date ===
      secondOption.estimatedDeliveryDate?.date &&
    firstOption.estimatedDeliveryDate?.time ===
      secondOption.estimatedDeliveryDate?.time
  );
}

const isAbortError = (error: unknown) => {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
};

export const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState<ShoppingCart>({
    cartItems: [],
  });
  const [savedProducts, setSavedProducts] = useState<Product[]>([]);
  const cartPdfComponentRef = useRef<HTMLDivElement>(null);
  const cartReactToPrintFn = useReactToPrint({
    contentRef: cartPdfComponentRef,
  });
  const [shippingCostsLoading, setShippingCostsLoading] = useState(false);
  const [, setShippingCostsError] = useState<string | null>(null);
  const pingShoppingCartUpdateNotification = useNotifications(
    (state) => state.incrementShoppingCartUpdateNotificationCount,
  );
  const cartRef = useRef<ShoppingCart>({
    cartItems: [],
  });
  const latestCartMutationIdRef = useRef(0);
  const shippingRefreshAbortControllerRef = useRef<AbortController | null>(
    null,
  );
  const [promotionCodeInput, setPromotionCodeInput] = useState("");
  const [appliedPromotionCode, setAppliedPromotionCode] = useState<
    string | null
  >(null);
  const [promotionCodeLoading, setPromotionCodeLoading] = useState(false);
  const [promotionCodeFeedback, setPromotionCodeFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [promotionConfirmationMessage, setPromotionConfirmationMessage] =
    useState<string | null>(null);
  const [shareCartLoading, setShareCartLoading] = useState(false);
  const [shareCartFeedback, setShareCartFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [sharedCartLink, setSharedCartLink] = useState<string | null>(null);
  const [savedCartLoading, setSavedCartLoading] = useState(false);
  const [savedCartError, setSavedCartError] = useState<string | null>(null);
  const loadedSavedCartIdRef = useRef<string | null>(null);

  // Quoting, as configured internally. Absent config means the stage is off or
  // unreachable, and the button simply does not appear.
  const { authUser } = useFirebaseAuthentication();
  const [quoteConfig, setQuoteConfig] = useState<QuoteFlowConfig | null>(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [revising, setRevising] = useState<BeingRevised | null>(null);

  useEffect(() => {
    loadQuoteFlowConfig()
      .then(setQuoteConfig)
      .catch(() => setQuoteConfig(null));
  }, []);

  // Set when a saved quote was reopened, so saving raises the next revision of
  // it rather than starting an unrelated quote.
  useEffect(() => {
    setRevising(quoteBeingRevised());
  }, []);

  const stopRevising = () => {
    forgetRevisedQuote();
    setRevising(null);
  };

  const posthog = usePostHog();
  const cartItems = cart.cartItems;
  const totalShippingOverride = cart.totalShippingOverride;
  const checkoutDefaults = cart.checkoutDefaults;
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const showAdvancedCartOptions = searchParams.has("advancedCartOptions");
  const savedCartId = searchParams.get("savedCartId")?.trim() || null;

  const clearPromotionCodeState = ({
    message,
    keepInput = false,
  }: {
    message?: string;
    keepInput?: boolean;
  } = {}) => {
    if (!keepInput) {
      setPromotionCodeInput("");
    }
    setAppliedPromotionCode(null);
    setPromotionConfirmationMessage(null);
    clearStoredPromotionCode();
    setPromotionCodeFeedback(
      message
        ? {
            type: "error",
            message,
          }
        : null,
    );
  };

  useEffect(() => {
    captureAnalyticsEvent(posthog, "viewed_cart", {
      cartItemCount: cartItems.length,
      hasPricedItems: cartItems.some((item) => itemCountsAsPriced(item)),
      cartProductIds: cartItems.map((item) => item.product.id),
    });
  }, [cartItems, posthog]);

  const itemCountsAsPriced = (item: CartItem) => {
    const computedPrice = convertPriceValueAndUnit(
      item.product,
      item.quantityRequired?.unit || "",
    );
    if (!computedPrice || !computedPrice.value) {
      return false;
    }

    // optionally, if configured, we also do not consider items with no shipping
    // cost available as priced
    if (
      process.env.REACT_APP_PREVENT_NO_SHIPPING_FROM_CHECKOUT === "true" &&
      !totalShippingOverride &&
      item.shippingCost?.available === false
    ) {
      return false;
    }
    return true;
  };

  const cartItemsWithPrice = cartItems.filter(itemCountsAsPriced);
  const cartItemsWithoutPrice = cartItems.filter(
    (item) => !itemCountsAsPriced(item),
  );

  const handlePromotionCodeInputChange = (value: string) => {
    setPromotionCodeInput(value);
    setPromotionCodeFeedback(null);

    if (value.trim().toUpperCase() !== appliedPromotionCode) {
      clearPromotionCodeState({ keepInput: true });
    }
  };

  const applyPromotionCode = async () => {
    if (!promotionCodeInput.trim()) {
      clearPromotionCodeState({
        message: "Enter a promotional code to apply it.",
      });
      return;
    }

    setPromotionCodeLoading(true);
    setPromotionCodeFeedback(null);

    try {
      const { promotionCode, confirmationMessage } =
        await validatePromotionCode(promotionCodeInput);

      setPromotionCodeInput(promotionCode);
      setAppliedPromotionCode(promotionCode);
      setPromotionConfirmationMessage(confirmationMessage || null);
      storePromotionCode({
        promotionCode,
        confirmationMessage,
      });
      setPromotionCodeFeedback({
        type: "success",
        message: `Promotion applied: ${promotionCode}`,
      });
      captureAnalyticsEvent(posthog, "applied_promotion_code", {
        promotionCode,
      });
    } catch (error) {
      console.error("Error validating promotion code:", error);
      clearPromotionCodeState({
        message: "That promotional code is invalid.",
        keepInput: true,
      });
      captureAnalyticsEvent(posthog, "failed_to_apply_promotion_code", {
        promotionCode: promotionCodeInput,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setPromotionCodeLoading(false);
    }
  };

  const storeCartLocally = (nextCart: ShoppingCart) => {
    const normalizedCart = normalizeCart(nextCart);

    console.log("Storing cart locally:", normalizedCart);
    cartRef.current = normalizedCart;
    setCart(normalizedCart);
    persistShoppingCart(normalizedCart);
  };

  const beginCartMutation = () => {
    latestCartMutationIdRef.current += 1;
    shippingRefreshAbortControllerRef.current?.abort();
    shippingRefreshAbortControllerRef.current = null;

    return latestCartMutationIdRef.current;
  };

  const refreshCartItems = async (
    items: CartItem[],
    forceReload = false,
    mutationId = latestCartMutationIdRef.current,
  ) => {
    const abortController = new AbortController();
    shippingRefreshAbortControllerRef.current = abortController;
    setShippingCostsLoading(true);
    setShippingCostsError(null);

    try {
      const updatedItems = await fetchShippingCosts(
        items,
        forceReload,
        abortController.signal,
      );

      if (
        abortController.signal.aborted ||
        latestCartMutationIdRef.current !== mutationId
      ) {
        return null;
      }

      return updatedItems;
    } catch (error) {
      if (isAbortError(error)) {
        return null;
      }
      throw error;
    } finally {
      if (shippingRefreshAbortControllerRef.current === abortController) {
        shippingRefreshAbortControllerRef.current = null;
      }
      if (latestCartMutationIdRef.current === mutationId) {
        setShippingCostsLoading(false);
      }
    }
  };

  const persistCart = async (
    nextCart: ShoppingCart,
    {
      forceReloadShipping = false,
      notify = false,
    }: {
      forceReloadShipping?: boolean;
      notify?: boolean;
    } = {},
  ) => {
    const mutationId = beginCartMutation();
    storeCartLocally(nextCart);

    if (notify) {
      pingShoppingCartUpdateNotification();
    }

    if (nextCart.totalShippingOverride || !forceReloadShipping) {
      setShippingCostsLoading(false);
      return;
    }

    const updatedShippingCosts = await refreshCartItems(
      nextCart.cartItems,
      true,
      mutationId,
    );
    if (updatedShippingCosts) {
      storeCartLocally({
        ...nextCart,
        cartItems: updatedShippingCosts,
      });
    }
  };

  const syncCart = async (nextCart: ShoppingCart) => {
    if (nextCart.cartItems.length === 0) {
      storeCartLocally(nextCart);
      setShippingCostsLoading(false);
      return;
    }

    const mutationId = beginCartMutation();
    storeCartLocally(nextCart);

    if (nextCart.totalShippingOverride) {
      setShippingCostsLoading(false);
      return;
    }

    const updatedShippingCosts = await refreshCartItems(
      nextCart.cartItems,
      false,
      mutationId,
    );
    if (updatedShippingCosts) {
      storeCartLocally({
        ...nextCart,
        cartItems: updatedShippingCosts,
      });
    }
  };

  const syncCartFromLocalStorage = async () => {
    const parsedCart = parseStoredCart(localStorage.getItem("shoppingCart"));
    if (parsedCart.cartItems.length === 0) {
      return;
    }

    await syncCart(parsedCart);
  };

  // Reopening a saved quote replaces what is in the cart. It can happen while
  // this page is already open, from the side panel, so the contents and the
  // banner are picked up from the event rather than only on mount.
  useEffect(() => {
    const reopened = () => {
      setRevising(quoteBeingRevised());
      void syncCartFromLocalStorage();
    };
    window.addEventListener(QUOTE_REOPENED_EVENT, reopened);
    return () => window.removeEventListener(QUOTE_REOPENED_EVENT, reopened);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!savedCartId) {
      syncCartFromLocalStorage();
    }

    const saved = localStorage.getItem("savedProducts");
    if (saved) {
      setSavedProducts(JSON.parse(saved));
    }

    const storedPromotionCode = getStoredPromotionCode();
    if (storedPromotionCode) {
      setPromotionCodeInput(storedPromotionCode.promotionCode);
      setPromotionCodeLoading(true);

      void validatePromotionCode(storedPromotionCode.promotionCode)
        .then(({ promotionCode, confirmationMessage }) => {
          setPromotionCodeInput(promotionCode);
          setAppliedPromotionCode(promotionCode);
          setPromotionConfirmationMessage(confirmationMessage || null);
          setPromotionCodeFeedback(null);
          storePromotionCode({
            promotionCode,
            confirmationMessage,
          });
        })
        .catch((error) => {
          console.error("Error validating stored promotion code:", error);
          clearPromotionCodeState({
            message: `Your saved promotional code ${storedPromotionCode.promotionCode} is no longer valid and has been removed.`,
          });
        })
        .finally(() => {
          setPromotionCodeLoading(false);
        });
    }
    // Only load the cart items once when the component mounts.
    // We want to avoid reloading the cart items when local storage changes
    // (e.g. a different tab updates the cart) becasue a) the user is going
    // through a payment flow and we don't want to introduce any surprises,
    // b) we will clear local storage when the payment is successful but
    // it might make sense to keep the items displayed here until the user
    // navigates away from the cart page.
  }, []);

  // Signed in: the cart lives on the account as well, shared with coworkers.
  // An empty local cart takes the account's copy; a coworker's newer edit
  // replaces what this browser held; otherwise the local cart is pushed up.
  useEffect(() => {
    if (!authUser || savedCartId) return;
    let live = true;
    void reconcileCartWithAccount()
      .then((fromAccount) => {
        if (!live || !fromAccount) return;
        void syncCart(fromAccount);
      })
      .catch((error) => console.warn("Account cart check failed:", error));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, savedCartId]);

  useEffect(() => {
    if (!authUser || appliedPromotionCode) return;
    let live = true;
    void (async () => {
      try {
        const response = await sendAuthenticatedRequest(
          `${process.env.REACT_APP_BF_API_BASE_URL}/promotions/account`,
          { method: "GET" },
        );
        if (!response.ok) return;
        const data = await response.json();
        const code = String(data?.promo?.code || "").trim();
        if (!live || !code || code === "ACCOUNT-FREE-SHIPPING") return;
        const { promotionCode, confirmationMessage } =
          await validatePromotionCode(code);
        if (!live) return;
        setPromotionCodeInput(promotionCode);
        setAppliedPromotionCode(promotionCode);
        setPromotionConfirmationMessage(confirmationMessage || null);
        storePromotionCode({ promotionCode, confirmationMessage });
      } catch {
        /* account offer is best-effort */
      }
    })();
    return () => {
      live = false;
    };
  }, [authUser, appliedPromotionCode]);

  useEffect(() => {
    return () => {
      shippingRefreshAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!savedCartId || loadedSavedCartIdRef.current === savedCartId) {
      return;
    }

    loadedSavedCartIdRef.current = savedCartId;
    setSavedCartLoading(true);
    setSavedCartError(null);

    void sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/cart/share/${encodeURIComponent(savedCartId)}`,
      {
        method: "GET",
      },
    )
      .then((response) => response.json())
      .then((data) => normalizeCart(data))
      .then(async (sharedCart) => {
        await syncCart(sharedCart);
        setShareCartFeedback({
          type: "success",
          message: "Loaded shared cart.",
        });
      })
      .catch((error) => {
        console.error("Failed to load shared cart:", error);
        setSavedCartError("Could not load the shared cart.");
      })
      .finally(() => {
        setSavedCartLoading(false);
      });
  }, [savedCartId]);

  const fetchShippingCosts = async (
    items: CartItem[],
    forceReload = false,
    signal?: AbortSignal,
  ) => {
    if (!items || items.length === 0) {
      return items;
    }

    if (cartRef.current.totalShippingOverride) {
      return items;
    }

    if (
      !forceReload &&
      items.every(
        (item) =>
          item.shippingCost?.available &&
          item.shippingOptions &&
          item.shippingOptions.length > 0,
      )
    ) {
      // All items already have shipping options, so we can just normalize the
      // selected shipping preference locally.
      return items.map((item) => {
        const selectedShippingOption =
          getSelectedShippingOptionPreference(item);
        const option =
          selectedShippingOption === "fastest"
            ? pickFastShippingOption(item.shippingOptions)
            : pickCheapestShippingOption(item.shippingOptions);

        if (!option) {
          return item;
        }

        return {
          ...item,
          selectedShippingOption,
          shippingCost: buildShippingCost(option, selectedShippingOption),
        };
      });
    }

    return await sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/cart/shippingCosts`,
      {
        method: "POST",
        signal,
        jsonBody: {
          cartItems: items,
          forceReload,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.shippingBilling) {
          shippingBilling = data.shippingBilling as ShippingBillingPolicy;
        }
        return (data.cartItems || []) as CartItem[];
      })
      .then((updatedItems) => {
        console.log("Fetched shipping costs for items:", updatedItems);
        return updatedItems.map((ci) =>
          applyShippingOptionToCartItem(
            ci,
            pickCheapestShippingOption(ci.shippingOptions),
            "cheapest",
          ),
        );
      })
      .catch((error) => {
        if (isAbortError(error)) {
          throw error;
        }
        console.error("Error fetching shipping costs:", error);
        setShippingCostsError("Failed to fetch shipping costs");
        return items;
      });
  };

  // Cheapest ("economy") option overall — including non-guaranteed services.
  // Freight economy rates are frequently non-guaranteed, and we still want to
  // surface (and charge) them; the guaranteed pick below covers the fast tier.
  const pickCheapestShippingOption = (
    options: ShipmentOption[] | undefined,
  ): ShipmentOption | undefined => {
    if (!options || options.length === 0) {
      return undefined;
    }
    return options.reduce((cheapest, option) =>
      option.totalCharges.monetaryValue < cheapest.totalCharges.monetaryValue
        ? option
        : cheapest,
    );
  };

  const stitchIsoDateTime = (date: string, time: string) => {
    if (!date) return "Z";
    if (!time) return date;
    return `${date}T${time}`;
  };

  // Fast tier: the earliest-delivery guaranteed option (the paid/premium
  // service for parcel, or the guaranteed freight service).
  const pickFastestGuaranteedShippingOption = (
    options: ShipmentOption[] | undefined,
  ): ShipmentOption | undefined => {
    if (!options || options.length === 0) {
      return undefined;
    }
    const guaranteedOptions = options.filter((option) => option.isGuaranteed);
    if (guaranteedOptions.length === 0) {
      return undefined;
    }
    const fastestOption = guaranteedOptions.reduce((fastest, option) => {
      if (!option.estimatedDeliveryDate) {
        return fastest;
      }
      if (!fastest.estimatedDeliveryDate) {
        return option;
      }
      if (
        stitchIsoDateTime(
          option.estimatedDeliveryDate?.date || "",
          option.estimatedDeliveryDate?.time || "",
        ) <
        stitchIsoDateTime(
          fastest.estimatedDeliveryDate?.date || "",
          fastest.estimatedDeliveryDate?.time || "",
        )
      ) {
        return option;
      }
      return fastest;
    }, guaranteedOptions[0]);

    return fastestOption;
  };

  // The "faster, paid" tier the buyer can upgrade to. An always-billed option
  // (freight) is itself the upgrade — the backend only surfaces it when the
  // configured conditions are met, by default when it delivers sooner than the
  // free option. For parcel we keep the fastest-guaranteed premium behavior.
  const pickFastShippingOption = (
    options: ShipmentOption[] | undefined,
  ): ShipmentOption | undefined => {
    if (!options || options.length === 0) {
      return undefined;
    }
    const freightOption = options.find(isAlwaysBilledOption);
    if (freightOption) {
      return freightOption;
    }
    return pickFastestGuaranteedShippingOption(options);
  };

  const applyShippingOptionToCartItem = (
    item: CartItem,
    option: ShipmentOption | undefined,
    selectedShippingOption?: ShippingSelectionPreference,
  ): CartItem => {
    console.log(`Applying shipping option to item ${item.product.id}:`, {
      option,
      selectedShippingOption,
    });
    if (!option) {
      return {
        ...item,
        selectedShippingOption,
        shippingCost: undefined,
      };
    }
    return {
      ...item,
      selectedShippingOption,
      shippingCost: buildShippingCost(
        option,
        selectedShippingOption || "cheapest",
      ),
    };
  };

  const formatEstimatedDeliveryDate = (estimatedDeliveryDate?: string) => {
    if (!estimatedDeliveryDate) {
      return "Date unavailable";
    }

    const parsedDate = new Date(estimatedDeliveryDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return estimatedDeliveryDate;
    }

    return parsedDate.toLocaleDateString();
  };

  const formatShipmentOptionDeliveryDate = (option?: ShipmentOption) => {
    if (!option?.estimatedDeliveryDate?.date) {
      return "Date unavailable";
    }

    return formatEstimatedDeliveryDate(
      stitchIsoDateTime(
        option.estimatedDeliveryDate.date,
        option.estimatedDeliveryDate.time || "",
      ),
    );
  };

  const handleShippingOptionSelection = async (
    item: CartItem,
    selectedShippingOption: ShippingSelectionPreference,
  ) => {
    const option =
      selectedShippingOption === "fastest"
        ? pickFastShippingOption(item.shippingOptions)
        : pickCheapestShippingOption(item.shippingOptions);

    const updatedCartItems = cartRef.current.cartItems.map((cartItem) => {
      if (cartItem.product.id !== item.product.id) {
        return cartItem;
      }

      return applyShippingOptionToCartItem(
        cartItem,
        option,
        selectedShippingOption,
      );
    });

    await persistCart(
      {
        cartItems: updatedCartItems,
      },
      { notify: true },
    );
  };

  const fetchShippingCostsWithFakeFallback = async (
    items: CartItem[],
    forceReload = false,
  ) => {
    const actualCosts = await fetchShippingCosts(items, forceReload);

    return actualCosts.map((item, index) => {
      if (item.shippingCost?.available) {
        console.log(`Item ${index} has shipping cost:`, item.shippingCost);
        return item;
      }
      const computedPrice = convertPriceValueAndUnit(
        item.product,
        item.quantityRequired?.unit || "",
      );
      if (!computedPrice || !computedPrice.value) {
        console.log(`Item ${index} has no price, skipping fake shipping cost`);
        return item;
      }
      // If we have a price but no shipping cost, we can provide a fake shipping cost
      // for demo purposes. This is only used if the API call to fetch shipping costs fails.
      return {
        ...item,
        shippingCost: {
          available: true,
          // Fake shipping cost as a random percentage of the item price, between 5% and 35%
          cost:
            (Math.random() * 0.3 + 0.05) *
              computedPrice.value *
              (item.quantityRequired?.value || 0) || 0,
          estimatedDeliveryDate: new Date(
            Date.now() + (Math.random() * 7 + 3) * 24 * 60 * 60 * 1000,
          ).toISOString(),
        },
      } as CartItem;
    });
  };

  const singlePriceElem = (item: CartItem) => {
    const computedPrice = convertPriceValueAndUnit(
      item.product,
      item.quantityRequired?.unit || "",
    ) || { value: 0, unit: item.product.price?.unit || "unit" };
    if (computedPrice.value) {
      const totalItemPrice =
        computedPrice.value * (item.quantityRequired?.value || 0);
      return (
        <span className="text-sm text-gray-900">
          {formatMoney(totalItemPrice, getCartItemCurrency(item))}
        </span>
      );
    } else {
      return (
        <span className="text-xs text-gray-500">Price provided later</span>
      );
    }
  };

  /**
   * Buying a quote whose line offers alternatives: the customer switches
   * the line to one of them. The server issues the quote's next version with
   * that item on the line, and the cart reloads from it so the prices stay
   * the server's.
   */
  const [switchingTo, setSwitchingTo] = useState("");
  const switchToAlternative = async (item: CartItem, productId: string) => {
    const buying = quoteBeingBought();
    if (!buying?.id) return;
    setSwitchingTo(productId);
    try {
      const next = await chooseQuoteAlternative(buying.id, {
        lineProductId: String(item.product?.id || ""),
        productId,
      });
      reopenInCart(next, { revising: false });
    } catch (e) {
      console.error("Could not switch the quote line", e);
      window.alert(
        e instanceof Error ? e.message : "Could not switch to that option. Please try again.",
      );
    } finally {
      setSwitchingTo("");
    }
  };

  const alternativesDisplay = (item: CartItem) => {
    const alternatives = item.alternatives || [];
    if (!alternatives.length || !quoteBeingBought()?.id) return null;
    return (
      <div className="mt-1 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
        <p className="text-xs font-medium text-amber-900">
          Other options for this item - pick one to switch (your quote gets a new version):
        </p>
        <ul className="mt-1 space-y-1">
          {alternatives.map((alt) => (
            <li key={alt.product?.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-800">
              <span className="min-w-0">
                {alt.product?.summary || "Option"}
                {typeof alt.lineAmount === "number" && (
                  <span className="ml-2 font-semibold text-gray-900">
                    {formatMoney(alt.lineAmount, getCartItemCurrency(item))}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="rounded border border-amber-400 px-2 py-0.5 text-[0.7rem] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                disabled={!!switchingTo}
                onClick={() => void switchToAlternative(item, String(alt.product?.id || ""))}
              >
                {switchingTo === alt.product?.id ? "Switching..." : "Choose this one"}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const displayItemRow = (item: CartItem) => {
    const shippingDisplay = () => {
      if (totalShippingOverride) {
        return null;
      }

      const cheapestOption = pickCheapestShippingOption(item.shippingOptions);
      const fastestOption = pickFastShippingOption(item.shippingOptions);
      const shippingOptions: {
        key: ShippingSelectionPreference;
        label: string;
        option: ShipmentOption;
      }[] = [];

      if (cheapestOption) {
        shippingOptions.push({
          key: "cheapest" as const,
          label:
            cheapestOption.displayLabel ||
            (isAlwaysBilledOption(cheapestOption) ? "Standard" : "Free"),
          option: cheapestOption,
        });
      }

      if (
        fastestOption &&
        !areSameShippingOptions(cheapestOption, fastestOption)
      ) {
        // A freight upgrade is surfaced only when it beats the free option's
        // lead time, so label it by that. Parcel keeps the "Fastest" premium
        // tier. Either can be renamed in the shipping configuration.
        const fastLabel = isAlwaysBilledOption(fastestOption)
          ? fastestOption.isGuaranteed
            ? "Faster (guaranteed)"
            : "Faster"
          : "Fastest";
        shippingOptions.push({
          key: "fastest" as const,
          label: fastestOption.displayLabel || fastLabel,
          option: fastestOption,
        });
      }

      const selectedShippingOption = getSelectedShippingOptionPreference(item);

      if (shippingCostsLoading) {
        return (
          <span className="text-xs text-gray-400">Shipping: Loading...</span>
        );
      }

      if (shippingOptions.length > 0) {
        return (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-gray-500">Shipping options</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {shippingOptions.map(({ key, label, option }) => {
                const isSelected = selectedShippingOption === key;
                const charge = getShipmentOptionCharge(option, key);

                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() =>
                      void handleShippingOptionSelection(item, key)
                    }
                    className={`rounded-md border px-3 py-2 text-left transition ${
                      isSelected
                        ? "border-[#00062e] bg-[#00062e]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-900">
                        {label}
                      </span>
                      <span className="text-xs font-semibold text-gray-900">
                        {charge === 0
                          ? "Free"
                          : formatMoney(
                              charge,
                              option.totalCharges.currencyCode,
                            )}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Est. delivery: {formatShipmentOptionDeliveryDate(option)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      if (
        item.shippingCost &&
        item.shippingCost.available &&
        item.shippingCost.cost !== undefined
      ) {
        return (
          <span className="text-xs text-gray-600">
            Shipping:{" "}
            {getShippingChargeAmount(item) === 0
              ? "Free"
              : formatMoney(
                  getShippingChargeAmount(item),
                  getCartItemCurrency(item),
                )}
            {item.shippingCost.estimatedDeliveryDate &&
              ` (Est. delivery: ${formatEstimatedDeliveryDate(item.shippingCost.estimatedDeliveryDate)})`}
          </span>
        );
      }

      return (
        <span className="text-xs text-gray-400">
          Could not estimate shipping costs and lead time
        </span>
      );
    };

    return (
      <li key={item.product.id} className="px-4 py-4 sm:px-6">
        <div className="flex min-w-0 flex-col space-y-1">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-start sm:gap-x-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Link
                  to={`/product?id=${item.product?.id}`}
                  state={{ previousUrl: window.location.href }}
                  className="min-w-0"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {/* Full listing summary (material, standard, dimensions) so
                        the buyer can tell exactly what they are buying — the
                        same description the quote and order documents show. */}
                    {item.product?.summary ||
                      `${(item.product?.material?.dbMaterials || [""])[0]} ${
                        item.product?.shape || ""
                      }`.trim()}
                  </span>
                </Link>
                <span className="text-sm text-gray-500">
                  Ship to{" "}
                  {formatAddress(
                    item.deliveryAddress,
                    AddressFormat.CityStateZip,
                  )}
                </span>
              </div>
            </div>
            <div className="whitespace-nowrap text-left sm:text-right">
              {singlePriceElem(item)}
            </div>
            <div className="flex justify-start sm:justify-end">
              <button
                type="button"
                className="cursor-pointer"
                onClick={handleRemoveItemFn(item)}
              >
                <TrashIcon className="mt-1 h-3 w-3 text-gray-600 hover:text-red-500" />
              </button>
            </div>
          </div>
          <div>
            <CartItemQuantityEditor
              item={item}
              onUpdateQuantity={handleUpdateItemQuantity}
            />
          </div>
          {alternativesDisplay(item)}
          <div>{shippingDisplay()}</div>
        </div>
      </li>
    );
  };

  const handleRemoveItemFn = (item: CartItem) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      console.log("Removing item from cart:", item);
      const newCartItems = cartRef.current.cartItems.filter(
        (cartItem) => cartItem.product.id !== item.product.id,
      );
      void persistCart(
        {
          cartItems: newCartItems,
        },
        { notify: true },
      );
    };
  };

  const handleUpdateItemQuantity = async (
    item: CartItem,
    quantityInput: string,
    quantityUnit?: string,
  ) => {
    const updatedCartItems = cartRef.current.cartItems.map((cartItem) => {
      if (cartItem.product.id !== item.product.id) {
        return cartItem;
      }

      if (cartItem.quantityRequired) {
        return {
          ...cartItem,
          quantityRequired: {
            ...cartItem.quantityRequired,
            value: parseFloat(quantityInput),
            unit: quantityUnit || cartItem.quantityRequired.unit,
          },
          selectedShippingOption: undefined,
          shippingOptions: undefined,
          shippingCost: undefined,
        };
      }

      return {
        ...cartItem,
        quantityRequiredString: quantityInput,
        selectedShippingOption: undefined,
        shippingOptions: undefined,
        shippingCost: undefined,
      };
    });

    await persistCart(
      {
        cartItems: updatedCartItems,
      },
      {
        forceReloadShipping: !!item.quantityRequired,
        notify: true,
      },
    );
  };

  const totalShippingOverrideElement = () => {
    if (!totalShippingOverride) {
      return null;
    }

    return (
      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900">Shipping</span>
            <span className="text-sm font-semibold text-gray-900">
              {getShipmentOptionChargeAmount(totalShippingOverride) === 0
                ? "Free"
                : formatMoney(
                    getShipmentOptionChargeAmount(totalShippingOverride),
                    totalShippingOverride.totalCharges.currencyCode,
                  )}
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-600">
            Est. delivery:{" "}
            {formatShipmentOptionDeliveryDate(totalShippingOverride)}
          </div>
        </div>
      </div>
    );
  };

  const handleSaveAdvancedCartOptions = async ({
    nextTotalShippingOverride,
    nextCheckoutDefaults,
  }: {
    nextTotalShippingOverride?: ShipmentOption;
    nextCheckoutDefaults?: CartCheckoutDefaults;
  }) => {
    const normalizedCheckoutDefaults =
      nextCheckoutDefaults &&
      [nextCheckoutDefaults.name, nextCheckoutDefaults.email, nextCheckoutDefaults.company].some((value) => !!value?.trim())
        ? {
            name: nextCheckoutDefaults.name?.trim() || undefined,
            email: nextCheckoutDefaults.email?.trim() || undefined,
            company: nextCheckoutDefaults.company?.trim() || undefined,
            // Delivery details ride along untouched (they come from a quote).
            phone: cartRef.current.checkoutDefaults?.phone,
            deliveryContact: cartRef.current.checkoutDefaults?.deliveryContact,
            deliveryInstructions: cartRef.current.checkoutDefaults?.deliveryInstructions,
          }
        : undefined;

    const previousCart = cartRef.current;
    const nextCart: ShoppingCart = {
      ...previousCart,
      totalShippingOverride: nextTotalShippingOverride,
      checkoutDefaults: normalizedCheckoutDefaults,
    };

    if (previousCart.totalShippingOverride && !nextTotalShippingOverride) {
      await persistCart(
        {
          ...nextCart,
          cartItems: previousCart.cartItems.map((item) => ({
            ...item,
            selectedShippingOption: undefined,
            shippingOptions: undefined,
            shippingCost: undefined,
          })),
        },
        {
          forceReloadShipping: true,
          notify: true,
        },
      );
      return;
    }

    await persistCart(nextCart, { notify: true });
  };

  const handleClearTotalShippingOverride = async (
    nextCheckoutDefaults?: CartCheckoutDefaults,
  ) => {
    await handleSaveAdvancedCartOptions({
      nextTotalShippingOverride: undefined,
      nextCheckoutDefaults:
        nextCheckoutDefaults || cartRef.current.checkoutDefaults,
    });
  };

  const handleShareCart = async () => {
    if (cartRef.current.cartItems.length === 0) {
      setShareCartFeedback({
        type: "error",
        message: "Add items to the cart before sharing it.",
      });
      return;
    }

    setShareCartLoading(true);
    setShareCartFeedback(null);

    try {
      const response = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_BF_API_BASE_URL}/cart/share`,
        {
          method: "POST",
          jsonBody: cartRef.current,
        },
      );
      const data = (await response.json()) as { savedCartId?: string };
      if (!data.savedCartId) {
        throw new Error("Missing savedCartId");
      }

      const shareLink = `${window.location.origin}/cart?savedCartId=${encodeURIComponent(
        data.savedCartId,
      )}`;
      setSharedCartLink(shareLink);

      try {
        await navigator.clipboard.writeText(shareLink);
        setShareCartFeedback({
          type: "success",
          message: "Share link copied to clipboard.",
        });
      } catch (clipboardError) {
        console.error("Failed to copy share link:", clipboardError);
        setShareCartFeedback({
          type: "success",
          message: "Share link created.",
        });
      }
    } catch (error) {
      console.error("Failed to share cart:", error);
      setShareCartFeedback({
        type: "error",
        message: "Could not create a share link.",
      });
    } finally {
      setShareCartLoading(false);
    }
  };

  if (savedCartLoading && (!cartItems || cartItems.length === 0)) {
    return (
      <div className="text-center py-10 text-sm text-gray-500">
        Loading shared cart...
      </div>
    );
  }

  if ((!cartItems || cartItems.length === 0) && savedProducts.length === 0) {
    return (
      <div className="text-center py-10">
        <h2 className="text-lg font-semibold text-gray-900">
          Your cart is empty
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          Start adding items to your cart to see them here.
        </p>
        {savedCartError && (
          <p className="mt-3 text-sm text-red-500">{savedCartError}</p>
        )}
      </div>
    );
  }

  return (
    <>
      {cartItems.length > 0 && (
        <>
          <div className="px-4 py-5 sm:px-6">
            {savedCartLoading && (
              <div className="pb-3 text-sm text-gray-500">
                Loading shared cart...
              </div>
            )}
            {savedCartError && (
              <div className="pb-3 text-sm text-red-500">{savedCartError}</div>
            )}
            {process.env.REACT_APP_SHOW_CART_BACK_BUTTON === "true" && (
              <div className="pb-4">
                {/* A Back link to navigate backwards using react router dom, equivalent of the browser back button */}
                <button
                  onClick={() => navigate(-1)}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  &larr; Back
                </button>
              </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Cart
                </h3>
                {cartItemsWithPrice.length > 0 && (
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Review your selected items before proceeding to checkout.
                  </p>
                )}
                {revising && (
                  <p className="mt-2 max-w-2xl rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
                    You are changing quote{" "}
                    <span className="font-medium">{revising.reference}</span>.
                    Saving keeps that version readable and writes the next one.{" "}
                    <button
                      type="button"
                      className="text-blue-700 hover:underline"
                      onClick={stopRevising}
                    >
                      Start a separate quote instead
                    </button>
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <KonoGuaranteeBadge small bordered />
                  <AS9120Badge imgClassName="h-8" />
                </div>
                {showAdvancedCartOptions && (
                  <AdvancedCartOptionsSection
                    totalShippingOverride={totalShippingOverride}
                    checkoutDefaults={checkoutDefaults}
                    onSave={handleSaveAdvancedCartOptions}
                    onClear={handleClearTotalShippingOverride}
                  />
                )}
              </div>
              <div className="flex flex-col items-start gap-2 self-start">
                <div className="flex flex-wrap items-center gap-3">
                  {showAdvancedCartOptions && (
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                      onClick={() => void handleShareCart()}
                      disabled={shareCartLoading}
                    >
                      {shareCartLoading ? "Creating link..." : "Share cart"}
                    </button>
                  )}
                  {quoteConfig?.quote.show && cartItems.length > 0 && (
                    <button
                      type="button"
                      className="rounded-full border border-[#00062e] px-3 py-1 text-xs font-medium text-[#00062e] hover:bg-[#00062e] hover:text-white"
                      onClick={() => setQuoteDialogOpen(true)}
                    >
                      {revising ? "Save a new version" : "Get a quote"}
                    </button>
                  )}
                  <button
                    className="flex items-center text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                    onClick={cartReactToPrintFn}
                  >
                    <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
                    Download as PDF
                  </button>
                </div>
                {showAdvancedCartOptions && shareCartFeedback && (
                  <div
                    className={`text-xs ${
                      shareCartFeedback.type === "error"
                        ? "text-red-500"
                        : "text-gray-500"
                    }`}
                  >
                    {shareCartFeedback.message}
                  </div>
                )}
                {showAdvancedCartOptions && sharedCartLink && (
                  <a
                    href={sharedCartLink}
                    className="max-w-xs break-all text-xs text-[#00062e] underline"
                  >
                    {sharedCartLink}
                  </a>
                )}
              </div>
            </div>
            {authUser && (
              <CompanyCartsPanel
                viewerEmail={authUser.email}
                myCartItems={cartItems}
                onCopyIntoMyCart={(items) => {
                  const own = new Map(
                    cartRef.current.cartItems.map((item) => [item.product.id, item]),
                  );
                  for (const item of items) own.set(item.product.id, item);
                  void syncCart({
                    ...cartRef.current,
                    cartItems: Array.from(own.values()),
                  });
                }}
              />
            )}
          </div>
          <div className="border-t border-gray-200">
            {cartItemsWithPrice.length > 0 && (
              <div className="md:grid md:grid-cols-2 md:gap-2">
                <div>
                  <ul className="divide-y divide-gray-200 border-b border-gray-200">
                    {cartItemsWithPrice.map((item) => displayItemRow(item))}
                  </ul>
                  <div className="text-xs text-gray-500 mt-4">
                    You can also change the quantity in the product details
                    page.
                  </div>
                  {totalShippingOverrideElement()}
                  {FLAG_CART_PROMOTION_CODES && (
                    <PromotionCodeSection
                      promotionCode={promotionCodeInput}
                      appliedPromotionCode={appliedPromotionCode}
                      loading={promotionCodeLoading}
                      feedback={promotionCodeFeedback}
                      onPromotionCodeChange={handlePromotionCodeInputChange}
                      onApplyPromotionCode={applyPromotionCode}
                    />
                  )}
                </div>
                <div className="mt-6 md:mt-3">
                  <CheckoutForm
                    cartItems={cartItemsWithPrice}
                    checkoutDefaults={checkoutDefaults}
                    showPricing={true}
                    shippingCostsLoading={shippingCostsLoading}
                    promotionCodeLoading={promotionCodeLoading}
                    totalShippingOverride={totalShippingOverride}
                    promotionCode={appliedPromotionCode || undefined}
                    promotionConfirmationMessage={
                      promotionConfirmationMessage || undefined
                    }
                  />
                </div>
              </div>
            )}
            {cartItemsWithoutPrice.length > 0 && (
              <>
                <div className="mt-16">
                  <div className="mt-1 max-w-2xl text-sm text-gray-500">
                    Requesting pricing for the following items
                  </div>
                </div>
                <div className="md:grid md:grid-cols-2 md:gap-2">
                  <div>
                    <ul className="divide-y divide-gray-200 border-b border-gray-200">
                      {cartItemsWithoutPrice.map((item) =>
                        displayItemRow(item),
                      )}
                    </ul>
                    <div className="text-xs text-gray-500 mt-4">
                      You can also change the quantity in the product details
                      page.
                    </div>
                    {totalShippingOverrideElement()}
                    {FLAG_CART_PROMOTION_CODES &&
                      cartItemsWithPrice.length === 0 && (
                        <PromotionCodeSection
                          promotionCode={promotionCodeInput}
                          appliedPromotionCode={appliedPromotionCode}
                          loading={promotionCodeLoading}
                          feedback={promotionCodeFeedback}
                          onPromotionCodeChange={handlePromotionCodeInputChange}
                          onApplyPromotionCode={applyPromotionCode}
                        />
                      )}
                  </div>
                  <div className="mt-6 md:mt-3">
                    <CheckoutForm
                      cartItems={cartItemsWithoutPrice}
                      checkoutDefaults={checkoutDefaults}
                      showPricing={false}
                      promotionCodeLoading={promotionCodeLoading}
                      totalShippingOverride={totalShippingOverride}
                      promotionCode={appliedPromotionCode || undefined}
                      promotionConfirmationMessage={
                        promotionConfirmationMessage || undefined
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="hidden print:block">
        <div ref={cartPdfComponentRef}>
          <style>{`
            @media print {
              .cart-pdf-container {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                color: #111827;
                line-height: 1.6;
                max-width: 100%;
                margin: 0;
                padding: 20px;
              }
              .cart-pdf-header {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px;
                color: #111827;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 10px;
              }
              .cart-pdf-section {
                margin-bottom: 30px;
              }
              .cart-pdf-section-title {
                font-size: 16px;
                font-weight: 600;
                margin-bottom: 15px;
                color: #374151;
              }
              .cart-pdf-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 16px;
                border: 1px solid #d1d5db;
              }
              .cart-pdf-table th {
                background-color: #f9fafb;
                padding: 12px;
                text-align: left;
                font-weight: 600;
                font-size: 14px;
                color: #374151;
                border-bottom: 1px solid #d1d5db;
                border-right: 1px solid #e5e7eb;
              }
              .cart-pdf-table th:last-child {
                border-right: none;
              }
              .cart-pdf-table td {
                padding: 12px;
                font-size: 14px;
                color: #111827;
                border-bottom: 1px solid #e5e7eb;
                border-right: 1px solid #e5e7eb;
                vertical-align: top;
              }
              .cart-pdf-table td:last-child {
                border-right: none;
              }
              .cart-pdf-table tr:last-child td {
                border-bottom: none;
              }
              .cart-pdf-table tr:nth-child(even) {
                background-color: #f9fafb;
              }
              .cart-pdf-product-name {
                font-weight: 500;
                color: #111827;
                margin-bottom: 2px;
              }
              .cart-pdf-quantity {
                color: #6b7280;
                font-size: 13px;
              }
              .cart-pdf-location {
                color: #6b7280;
                font-size: 13px;
              }
              .cart-pdf-price {
                font-weight: 500;
                color: #111827;
                text-align: right;
              }
              .cart-pdf-total {
                margin-top: 20px;
                padding-top: 15px;
                border-top: 2px solid #374151;
                text-align: right;
                font-size: 16px;
                font-weight: 600;
              }
              .cart-pdf-footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
                font-size: 12px;
                color: #6b7280;
                text-align: center;
              }
            }
          `}</style>
          <div className="cart-pdf-container">
            <h2 className="cart-pdf-header">Cart Summary from Kono</h2>

            {cartItemsWithPrice.length > 0 && (
              <div className="cart-pdf-section">
                <h3 className="cart-pdf-section-title">Items with Pricing</h3>
                <table className="cart-pdf-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Ship To</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItemsWithPrice.map((item) => {
                      const computedPrice = convertPriceValueAndUnit(
                        item.product,
                        item.quantityRequired?.unit || "",
                      ) || {
                        value: 0,
                        unit: item.product.price?.unit || "unit",
                      };
                      const totalItemPrice =
                        (computedPrice.value || 0) *
                        (item.quantityRequired?.value || 0);

                      return (
                        <tr key={item.product.id}>
                          <td>
                            <div className="cart-pdf-product-name">
                              {(item.product?.material?.dbMaterials || [""])[0]}{" "}
                              {item.product?.shape}
                            </div>
                          </td>
                          <td>
                            <div className="cart-pdf-quantity">
                              {item.quantityRequiredString ||
                                `${item.quantityRequired?.value} ${item.quantityRequired?.unit}`}
                            </div>
                          </td>
                          <td>
                            <div className="cart-pdf-location">
                              {formatAddress(
                                item.deliveryAddress,
                                AddressFormat.Full,
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="cart-pdf-price">
                              {formatMoney(
                                totalItemPrice,
                                item.product.price?.currency || "USD",
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="cart-pdf-total">
                  Total:{" "}
                  {formatMoney(
                    cartItemsWithPrice.reduce((total, item) => {
                      const computedPrice = convertPriceValueAndUnit(
                        item.product,
                        item.quantityRequired?.unit || "",
                      ) || {
                        value: 0,
                        unit: item.product.price?.unit || "unit",
                      };
                      return (
                        total +
                        (computedPrice.value || 0) *
                          (item.quantityRequired?.value || 0)
                      );
                    }, 0),
                    getCartItemsCurrency(cartItemsWithPrice),
                  )}
                </div>
              </div>
            )}

            {cartItemsWithoutPrice.length > 0 && (
              <div className="cart-pdf-section">
                <h3 className="cart-pdf-section-title">
                  Items Requiring Price Quote
                </h3>
                <table className="cart-pdf-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Ship To</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItemsWithoutPrice.map((item) => (
                      <tr key={item.product.id}>
                        <td>
                          <div className="cart-pdf-product-name">
                            {(item.product?.material?.dbMaterials || [""])[0]}{" "}
                            {item.product?.shape}
                          </div>
                        </td>
                        <td>
                          <div className="cart-pdf-quantity">
                            {item.quantityRequiredString ||
                              `${item.quantityRequired?.value} ${item.quantityRequired?.unit}`}
                          </div>
                        </td>
                        <td>
                          <div className="cart-pdf-location">
                            {formatAddress(
                              item.deliveryAddress,
                              AddressFormat.Full,
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="cart-pdf-quantity">
                            Price to be quoted
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="cart-pdf-footer">
              Generated from Kono - {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {savedProducts.length > 0 && (
        <>
          <div className="border-t border-gray-200 mt-8">
            <BookmarkedItems embedded={true} />
          </div>
        </>
      )}

      {quoteConfig?.quote.show && (
        <QuoteDialog
          open={quoteDialogOpen}
          onClose={() => setQuoteDialogOpen(false)}
          cart={cart}
          config={quoteConfig}
          signedIn={Boolean(authUser)}
          revisingQuoteId={revising?.id || null}
          defaults={{
            name: checkoutDefaults?.name || authUser?.displayName || "",
            company: checkoutDefaults?.company || "",
            email:
              checkoutDefaults?.email ||
              authUser?.email ||
              localStorage.getItem(LAST_USED_GUEST_EMAIL_KEY) ||
              "",
            phone: localStorage.getItem(LAST_USED_GUEST_PHONE_KEY) || "",
          }}
          onSaved={stopRevising}
        />
      )}
    </>
  );
};

const CartItemQuantityEditor = ({
  item,
  onUpdateQuantity,
}: {
  item: CartItem;
  onUpdateQuantity: (
    item: CartItem,
    quantityInput: string,
    quantityUnit?: string,
  ) => Promise<void>;
}) => {
  const currentQuantity = item.quantityRequired
    ? `${item.quantityRequired.value}`
    : (item.quantityRequiredString ?? "");
  const currentQuantityUnit = item.quantityRequired?.unit;
  const [quantityInput, setQuantityInput] = useState(currentQuantity);
  const [quantityUnit, setQuantityUnit] = useState(currentQuantityUnit);

  useEffect(() => {
    setQuantityInput(currentQuantity);
    setQuantityUnit(currentQuantityUnit);
  }, [currentQuantity, currentQuantityUnit]);

  const quantityError = item.quantityRequired
    ? getQuantityError({
        product: item.product,
        quantityRequired: quantityInput,
        quantityUnit,
      })
    : null;

  const availableUnits = useMemo(
    () =>
      item.quantityRequired
        ? getAvailableQuantityUnits({
            product: item.product,
            preferredUnit: quantityUnit || currentQuantityUnit,
          })
        : [],
    [currentQuantityUnit, item.product, item.quantityRequired, quantityUnit],
  );

  useEffect(() => {
    if (!item.quantityRequired) {
      return;
    }

    if (availableUnits.length === 0) {
      if (quantityUnit !== undefined) {
        setQuantityUnit(undefined);
      }
      return;
    }

    const resolved = resolveQuantityUnit(quantityUnit, availableUnits);
    if (resolved !== quantityUnit) {
      setQuantityUnit(resolved);
    }
  }, [availableUnits, item.quantityRequired, quantityUnit]);

  const quantityChanged =
    quantityInput !== currentQuantity || quantityUnit !== currentQuantityUnit;
  const updateQuantity = async () => {
    if (!quantityChanged || quantityError || quantityInput.trim() === "") {
      return;
    }

    if (item.quantityRequired && Number.isNaN(parseFloat(quantityInput))) {
      return;
    }

    await onUpdateQuantity(item, quantityInput, quantityUnit);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Qty</span>
        <input
          type={item.quantityRequired ? "number" : "text"}
          step={item.quantityRequired ? "any" : undefined}
          min={item.quantityRequired ? "0" : undefined}
          value={quantityInput}
          onChange={(event) => setQuantityInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void updateQuantity();
            }
          }}
          className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#00062e]"
        />
        {item.quantityRequired && (
          <UnitSelect
            options={availableUnits.map((unit) => ({
              value: unit,
              label: simpleFormatUnit(unit),
            }))}
            onChange={(value) => setQuantityUnit(value)}
            value={quantityUnit}
          />
        )}
        <button
          type="button"
          onClick={() => void updateQuantity()}
          disabled={
            !quantityChanged || !!quantityError || quantityInput.trim() === ""
          }
          className="rounded-full border border-[#00062e] px-3 py-1 text-xs font-semibold text-[#00062e] disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
        >
          Update
        </button>
      </div>
      {quantityError && (
        <div className="text-xs text-red-500">{quantityError}</div>
      )}
    </div>
  );
};

type CheckoutFormBasicData = {
  name: string;
  company: string;
  email: string;
};

const AdvancedCartOptionsSection = ({
  totalShippingOverride,
  checkoutDefaults,
  onSave,
  onClear,
}: {
  totalShippingOverride?: ShipmentOption;
  checkoutDefaults?: CartCheckoutDefaults;
  onSave: (args: {
    nextTotalShippingOverride?: ShipmentOption;
    nextCheckoutDefaults?: CartCheckoutDefaults;
  }) => Promise<void>;
  onClear: (nextCheckoutDefaults?: CartCheckoutDefaults) => Promise<void>;
}) => {
  const [name, setName] = useState(checkoutDefaults?.name || "");
  const [email, setEmail] = useState(checkoutDefaults?.email || "");
  const [company, setCompany] = useState(checkoutDefaults?.company || "");
  const [carrier, setCarrier] = useState(totalShippingOverride?.vendorId || "");
  const [serviceCode, setServiceCode] = useState(
    totalShippingOverride?.serviceCode || "",
  );
  const [quoteNumber, setQuoteNumber] = useState(
    totalShippingOverride?.vendorShipmentReference || "",
  );
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState(
    totalShippingOverride?.estimatedDeliveryDate?.date || "",
  );
  const [costInput, setCostInput] = useState(
    totalShippingOverride
      ? `${getShipmentOptionChargeAmount(totalShippingOverride)}`
      : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(checkoutDefaults?.name || "");
    setEmail(checkoutDefaults?.email || "");
    setCompany(checkoutDefaults?.company || "");
    setCarrier(totalShippingOverride?.vendorId || "");
    setServiceCode(totalShippingOverride?.serviceCode || "");
    setQuoteNumber(totalShippingOverride?.vendorShipmentReference || "");
    setEstimatedDeliveryDate(
      totalShippingOverride?.estimatedDeliveryDate?.date || "",
    );
    setCostInput(
      totalShippingOverride
        ? `${getShipmentOptionChargeAmount(totalShippingOverride)}`
        : "",
    );
    setError(null);
  }, [checkoutDefaults, totalShippingOverride]);

  const saveOverride = async () => {
    const hasShippingOverride = !!costInput.trim();
    const parsedCost = parseFloat(costInput);
    if (hasShippingOverride && (Number.isNaN(parsedCost) || parsedCost < 0)) {
      setError("Enter a valid shipping cost.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        nextTotalShippingOverride: hasShippingOverride
          ? {
              vendorId: carrier.trim() || "manual-override",
              serviceCode: serviceCode.trim() || undefined,
              vendorShipmentReference: quoteNumber.trim() || undefined,
              totalCharges: {
                currencyCode: "USD",
                monetaryValue: parsedCost,
              },
              estimatedDeliveryDate: estimatedDeliveryDate
                ? {
                    date: estimatedDeliveryDate,
                  }
                : undefined,
              isGuaranteed: true,
              vendorResponse: {
                source: "advancedCartOptions",
              },
            }
          : undefined,
        nextCheckoutDefaults: {
          name,
          email,
          company,
        },
      });
    } catch (saveError) {
      console.error("Failed to save advanced cart options:", saveError);
      setError("Failed to save advanced cart options.");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    setSaving(true);
    setError(null);

    try {
      await onClear({
        name,
        email,
        company,
      });
    } catch (clearError) {
      console.error("Failed to clear total shipping override:", clearError);
      setError("Failed to clear shipping override.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <h4 className="text-sm font-medium text-gray-900">
        Advanced cart options
      </h4>
      <p className="mt-1 text-sm text-gray-500">
        Override item-level shipping with one shipping option for the whole
        cart.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Name</span>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="Optional checkout default"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="Optional checkout default"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Company</span>
          <input
            type="text"
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="Optional checkout default"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">
            Shipping cost
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={costInput}
            onChange={(event) => setCostInput(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="25.00"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">
            Estimated delivery date
          </span>
          <input
            type="date"
            value={estimatedDeliveryDate}
            onChange={(event) => setEstimatedDeliveryDate(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Carrier</span>
          <input
            type="text"
            value={carrier}
            onChange={(event) => setCarrier(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="Manual override"
            disabled={saving}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">
            Service code
          </span>
          <input
            type="text"
            value={serviceCode}
            onChange={(event) => setServiceCode(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
            placeholder="GROUND"
            disabled={saving}
          />
        </label>
      </div>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-gray-700">Quote number</span>
        <input
          type="text"
          value={quoteNumber}
          onChange={(event) => setQuoteNumber(event.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#00062e] focus:outline-none focus:ring-1 focus:ring-[#003636]"
          placeholder="Optional reference"
          disabled={saving}
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => void saveOverride()}
          disabled={saving}
          className="inline-flex items-center justify-center rounded-md bg-[#00062e] px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save advanced options"}
        </button>
        {totalShippingOverride && (
          <button
            type="button"
            onClick={() => void clearOverride()}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Remove override
          </button>
        )}
      </div>
    </div>
  );
};

const PromotionCodeSection = ({
  promotionCode,
  appliedPromotionCode,
  loading,
  feedback,
  onPromotionCodeChange,
  onApplyPromotionCode,
}: {
  promotionCode: string;
  appliedPromotionCode: string | null;
  loading: boolean;
  feedback: {
    type: "success" | "error";
    message: string;
  } | null;
  onPromotionCodeChange: (value: string) => void;
  onApplyPromotionCode: () => void;
}) => {
  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <h4 className="text-sm font-medium text-gray-900">Promotional code</h4>
      <p className="mt-1 text-sm text-gray-500">
        Enter a promotional code before checkout.
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={promotionCode}
          onChange={(event) => onPromotionCodeChange(event.target.value)}
          placeholder="Enter your code"
          className="block w-full rounded-md border-gray-300 bg-white px-3 py-2 text-sm shadow-sm border focus:outline-none focus:ring-1 focus:ring-[#003636] focus:border-[#00062e]"
          disabled={loading}
        />
        <button
          type="button"
          onClick={onApplyPromotionCode}
          disabled={loading || !promotionCode.trim()}
          className="inline-flex items-center justify-center rounded-md bg-[#00062e] px-4 py-2 text-sm font-bold text-[#eeeeee] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Applying..." : "Apply"}
        </button>
      </div>
      {feedback && (
        <p
          className={`mt-3 text-sm ${
            feedback.type === "success" ? "text-green-600" : "text-red-500"
          }`}
        >
          {feedback.message}
        </p>
      )}
      {!feedback && appliedPromotionCode && (
        <p className="mt-3 text-sm text-green-600">
          Promotion applied: {appliedPromotionCode}
        </p>
      )}
    </div>
  );
};

const CheckoutForm = ({
  cartItems,
  checkoutDefaults,
  showPricing,
  shippingCostsLoading,
  promotionCodeLoading,
  totalShippingOverride,
  promotionCode,
  promotionConfirmationMessage,
}: {
  cartItems: CartItem[];
  checkoutDefaults?: CartCheckoutDefaults;
  showPricing: boolean;
  shippingCostsLoading?: boolean;
  promotionCodeLoading?: boolean;
  totalShippingOverride?: ShipmentOption;
  promotionCode?: string;
  promotionConfirmationMessage?: string;
}) => {
  const [checkoutPressed, setCheckoutPressed] = useState(false);
  const user = useUser((state) => state.user);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  // Who receives the goods and how: pre-filled from the quote's freight plan
  // when buying a quote, otherwise typed here. Optional; goes on the order.
  const [deliveryContact, setDeliveryContact] = useState({ company: "", name: "", phone: "", email: "" });
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    "credit_card" | "wire_transfer"
  >("credit_card");
  const [orderPaymentMethod, setOrderPaymentMethod] = useState<
    "credit_card" | "wire_transfer"
  >("credit_card");
  const [wireTransferRecipientEmail, setWireTransferRecipientEmail] = useState(
    "procurement@kono.ai",
  );
  const [
    orderPromotionConfirmationMessage,
    setOrderPromotionConfirmationMessage,
  ] = useState<string | null>(null);
  const posthog = usePostHog();
  const hasLoggedUnexpectedCheckoutState = useRef(false);

  const displayCurrency = getCartItemsCurrency(cartItems);

  const productSubtotal = cartItems.reduce((total, item) => {
    const computedPrice = convertPriceValueAndUnit(
      item.product,
      item.quantityRequired?.unit || "",
    ) || { value: 0, unit: item.product.price?.unit || "unit" };
    return (
      total + (computedPrice.value || 0) * (item.quantityRequired?.value || 0)
    );
  }, 0);

  const shippingTotal =
    showPricing && totalShippingOverride
      ? getShipmentOptionChargeAmount(totalShippingOverride)
      : cartItems.reduce((total, item) => {
          const computedPrice = convertPriceValueAndUnit(
            item.product,
            item.quantityRequired?.unit || "",
          ) || { value: 0, unit: item.product.price?.unit || "unit" };

          if (!computedPrice.value) {
            return total;
          }

          return total + getShippingChargeAmount(item);
        }, 0);

  const totalPrice = productSubtotal + shippingTotal;
  const allProductsHavePrice = cartItems.every(
    (item) => item.product.price && item.product.price.value !== undefined,
  );
  const checkoutRenderState = !checkoutPressed
    ? "idle"
    : !creatingOrder && !orderId
      ? "guest_details"
      : creatingOrder
        ? "creating_order"
        : orderId && clientSecret && !paid
          ? "payment"
          : orderId && (paid || !totalPrice)
            ? "complete"
            : "unexpected";

  const {
    register,
    handleSubmit,
    getValues: getUserDataValues,
    setValue,
    formState: { errors },
  } = useForm<CheckoutFormBasicData>();

  const createOrder = async (data: CheckoutFormBasicData, email: string) => {
    const name = (data.name || user?.displayName || "").trim();
    const company = (data.company || user?.company?.businessName || "").trim();

    if (!name) {
      setError("Please enter your name.");
      return;
    }
    if (!company) {
      setError("Please enter your company.");
      return;
    }

    setCreatingOrder(true);
    setError(null);
    const payload: OrderDetailsApiPayload = {
      cartItems: cartItems,
      name,
      company,
      email,
      phone: guestPhone || undefined,
      ...(Object.values(deliveryContact).some((value) => value.trim())
        ? {
            deliveryContact: {
              company: deliveryContact.company.trim() || undefined,
              name: deliveryContact.name.trim() || undefined,
              phone: deliveryContact.phone.trim() || undefined,
              email: deliveryContact.email.trim() || undefined,
            },
          }
        : {}),
      ...(deliveryInstructions.trim() ? { deliveryInstructions: deliveryInstructions.trim() } : {}),
      totalPrice,
      totalShippingOverride,
      searchQueryHistory: JSON.parse(
        localStorage.getItem("searchQueryHistory") || "[]",
      ),
      promotionCode,
      promotionConfirmationMessage,
      paymentMethod,
      ...(quoteBeingBought()?.id
        ? { quoteId: quoteBeingBought()!.id }
        : {}),
    };
    sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/create-order`,
      {
        method: "POST",
        jsonBody: payload,
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText || `Failed to create order (${response.status})`,
          );
        }
        return response.json();
      })
      .then((responseData) => {
        console.log("Order response:", responseData);
        setOrderId(responseData.orderId || null);
        setClientSecret(responseData.clientSecret || null);
        setOrderPaymentMethod(
          responseData.paymentMethod === "wire_transfer"
            ? "wire_transfer"
            : paymentMethod,
        );
        setWireTransferRecipientEmail(
          responseData.wireTransferRecipientEmail || "procurement@kono.ai",
        );
        setOrderPromotionConfirmationMessage(
          responseData.promotionConfirmationMessage || null,
        );
        stopBuyingQuote();
      })
      .catch((error) => {
        console.error("Error creating order:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Failed to create order. Please try again later.",
        );
      })
      .finally(() => {
        setCreatingOrder(false);
      });
  };

  useEffect(() => {
    // A kono.ai operator with a quote loaded to buy is looking at the
    // CUSTOMER's checkout — a preview of what the customer sees, or a purchase
    // on their behalf — so the quote's customer fills the fields, not the
    // operator's own profile. checkoutDefaults only carry a quote's customer
    // (seeded by the buy flow), so their presence is the signal.
    const hasQuoteCustomer =
      checkoutDefaults &&
      [checkoutDefaults.name, checkoutDefaults.email, checkoutDefaults.company].some((value) => !!value?.trim());
    const previewingAsCustomer =
      user?.email?.endsWith("@kono.ai") && hasQuoteCustomer;

    if (user?.email && !previewingAsCustomer) {
      setValue("name", user.displayName || checkoutDefaults?.name || "");
      setValue(
        "company",
        user.company?.businessName || checkoutDefaults?.company || "",
      );
      setGuestEmail(user.email);
      setValue("email", user.email);
      return;
    }

    if (previewingAsCustomer) {
      setValue("name", checkoutDefaults?.name || "");
      setValue("company", checkoutDefaults?.company || "");
      const customerEmail = checkoutDefaults?.email?.trim() || user?.email || "";
      setGuestEmail(customerEmail);
      setValue("email", customerEmail);
      return;
    }

    setValue("name", checkoutDefaults?.name || "");
    setValue("company", checkoutDefaults?.company || "");

    const configuredGuestEmail = checkoutDefaults?.email?.trim();
    const cachedGuestEmail =
      configuredGuestEmail ||
      localStorage.getItem(LAST_USED_GUEST_EMAIL_KEY) ||
      "";
    setGuestEmail(cachedGuestEmail);
    setValue("email", cachedGuestEmail);
  }, [checkoutDefaults, setValue, user]);

  useEffect(() => {
    setGuestPhone(checkoutDefaults?.phone || localStorage.getItem(LAST_USED_GUEST_PHONE_KEY) || "");
  }, [checkoutDefaults?.phone]);

  useEffect(() => {
    const contact = checkoutDefaults?.deliveryContact;
    const next = { company: contact?.company || "", name: contact?.name || "", phone: contact?.phone || "", email: contact?.email || "" };
    setDeliveryContact(next);
    setDeliveryInstructions(checkoutDefaults?.deliveryInstructions || "");
    setDeliveryOpen(Object.values(next).some(Boolean) || Boolean(checkoutDefaults?.deliveryInstructions));
  }, [checkoutDefaults?.deliveryContact, checkoutDefaults?.deliveryInstructions]);

  useEffect(() => {
    if (checkoutRenderState !== "unexpected") {
      hasLoggedUnexpectedCheckoutState.current = false;
      return;
    }

    if (hasLoggedUnexpectedCheckoutState.current) {
      return;
    }

    hasLoggedUnexpectedCheckoutState.current = true;

    posthog?.capture?.("checkout_unexpected_render_state", {
      checkoutPressed,
      creatingOrder,
      hasUser: Boolean(user),
      hasOrderId: Boolean(orderId),
      hasClientSecret: Boolean(clientSecret),
      paid,
      totalPrice,
      showPricing,
      shippingCostsLoading: Boolean(shippingCostsLoading),
      promotionCodeLoading: Boolean(promotionCodeLoading),
      cartItemCount: cartItems.length,
      allProductsHavePrice,
      hasError: Boolean(error),
      hasPromotionCode: Boolean(promotionCode),
    });

    console.error("Unexpected checkout render state", {
      checkoutPressed,
      creatingOrder,
      orderId,
      clientSecret,
      paid,
      totalPrice,
      showPricing,
      shippingCostsLoading,
      promotionCodeLoading,
      cartItemCount: cartItems.length,
      allProductsHavePrice,
      error,
    });
  }, [
    allProductsHavePrice,
    cartItems.length,
    checkoutPressed,
    checkoutRenderState,
    clientSecret,
    creatingOrder,
    error,
    orderId,
    paid,
    posthog,
    promotionCode,
    promotionCodeLoading,
    shippingCostsLoading,
    showPricing,
    totalPrice,
    user,
  ]);

  const basicDetailsSubmitHandler: SubmitHandler<CheckoutFormBasicData> = (
    data: CheckoutFormBasicData,
  ) => {
    // The typed address wins: the field is editable so the order's contact
    // can differ from the signed-in account.
    const email = data.email?.trim() || user?.email || guestEmail || "";
    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if (!user) {
      setGuestEmail(email);
      localStorage.setItem(LAST_USED_GUEST_EMAIL_KEY, email);
    }

    setError(null);
    console.log("Form submitted with data:", data);
    createOrder(data, email);
    captureAnalyticsEvent(posthog, "checkout_basic_details_submitted", {
      cartValue: totalPrice,
      cartItemCount: cartItems.length,
      promotionCode: promotionCode || undefined,
    });
    // validate email address format on the frontend before identifying in posthog
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !user) {
      identifyAnalyticsUser(posthog, email);
    }
  };

  const totalPriceElem = () => {
    if (shippingCostsLoading) {
      return (
        <div className="flex items-right justify-end space-x-2 mt-1">
          <ClipLoader
            size={14}
            color={"#00062e"}
            loading={true}
            className="mt-1"
          />
          <div className="text-sm text-gray-500">
            Calculating shipping costs...
          </div>
        </div>
      );
    }
    if (!showPricing) {
      return (
        <div className="text-sm text-gray-500">Full pricing provided later</div>
      );
    }
    if (allProductsHavePrice) {
      return (
        <div className="space-y-1">
          <div className="text-sm text-gray-500">
            <div className="flex items-center justify-end gap-2">
              <span>Subtotal:</span>
              <span>{formatMoney(productSubtotal, displayCurrency)}</span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <span>Shipping:</span>
              <span>
                {shippingTotal === 0
                  ? "Free"
                  : formatMoney(shippingTotal, displayCurrency)}
              </span>
            </div>
            {!totalShippingOverride &&
              cartItems.some((item) => !item.shippingCost?.available) && (
                <div className="text-xs text-gray-500">
                  Could not estimate shipping costs and lead times for some
                  items. You may still proceed to checkout and our team will be
                  in touch shortly.
                </div>
              )}
          </div>
          <div className="font-bold text-lg">
            Total: {formatMoney(totalPrice, displayCurrency)}
          </div>
        </div>
      );
    } else if (totalPrice > 0) {
      // This should never actually happen as CheckoutForm should only
      // receive all priced or all non-priced items, but we are handing it gracefully
      return (
        <>
          <div>
            <span className="font-bold text-lg">
              Total: {formatMoney(totalPrice, displayCurrency)}
            </span>{" "}
            to pay now
          </div>
          <div className="text-xs text-gray-500">
            Other prices provided later
          </div>
        </>
      );
    } else {
      return <div className="text-sm text-gray-500">Prices provided later</div>;
    }
  };

  const handlePaymentSuccess = () => {
    setPaid(true);
    // Empty on the account too, so coworkers do not see a cart already bought.
    persistShoppingCart({ cartItems: [] });
    clearStoredPromotionCode();
  };

  if (!checkoutPressed) {
    return (
      <>
        {" "}
        <button
          onClick={() => {
            setCheckoutPressed(true);
            captureAnalyticsEvent(posthog, "checkout_initiated", {
              cartValue: totalPrice,
              cartItemCount: cartItems.length,
              promotionCode: promotionCode || undefined,
            });
          }}
          type="button"
          className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={shippingCostsLoading || promotionCodeLoading}
        >
          {!!totalPrice && showPricing ? "Checkout" : "Request Pricing"}
        </button>
        <div className="text-right">{totalPriceElem()}</div>
      </>
    );
  }

  if (!creatingOrder && !orderId) {
    return (
      <form onSubmit={handleSubmit(basicDetailsSubmitHandler)}>
        <div className="isolate -space-y-px rounded-xl shadow-sm mt-3">
          <div className="relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e] rounded-xl rounded-b-none">
            <label
              htmlFor="input-name"
              className="block text-xs font-medium text-gray-900"
            >
              Name
            </label>
            <input
              id="input-name"
              type="text"
              placeholder="Your name"
              className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              {...register("name", { required: "Please enter your name." })}
            />
          </div>
          <div className="relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
            <label
              htmlFor="input-company"
              className="block text-xs font-medium text-gray-900"
            >
              Company
            </label>
            <input
              id="input-company"
              type="text"
              placeholder="Your company name"
              {...register("company", {
                required: "Please enter your company.",
              })}
              className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
            />
          </div>
          <div className="relative rounded-xl rounded-t-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
            <label
              htmlFor="input-email"
              className="block text-xs font-medium text-gray-900"
            >
              Email
            </label>
            {/* Editable even when signed in: the order's contact address is
                not always the account's — a buyer checking out a quote may
                route confirmations to procurement, or an operator may check
                out on the customer's behalf. */}
            <input
              id="input-email"
              type="email"
              placeholder="you@example.com"
              className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              {...register("email", {
                required: "Please enter your email.",
              })}
            />
          </div>
        </div>
        {(errors.name || errors.company || errors.email) && (
          <div className="mt-2 text-sm text-red-500">
            {errors.name?.message ||
              errors.company?.message ||
              errors.email?.message}
          </div>
        )}
        <div className="mt-4 rounded-xl border border-gray-300 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-900">
              Delivery contact and instructions
              <span className="ml-1 font-normal text-gray-500">(optional; goes to the carrier)</span>
            </div>
            <button
              type="button"
              className="text-xs text-blue-700 underline"
              onClick={() => setDeliveryOpen((open) => !open)}
            >
              {deliveryOpen ? "Hide" : "Add"}
            </button>
          </div>
          {deliveryOpen && (
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Receiving: company"
                value={deliveryContact.company}
                onChange={(e) => setDeliveryContact({ ...deliveryContact, company: e.target.value })}
              />
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Receiving: person's name"
                value={deliveryContact.name}
                onChange={(e) => setDeliveryContact({ ...deliveryContact, name: e.target.value })}
              />
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Receiving: phone"
                value={deliveryContact.phone}
                onChange={(e) => setDeliveryContact({ ...deliveryContact, phone: e.target.value })}
              />
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Receiving: email"
                type="email"
                value={deliveryContact.email}
                onChange={(e) => setDeliveryContact({ ...deliveryContact, email: e.target.value })}
              />
              <textarea
                className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
                rows={2}
                placeholder="Delivery instructions: hours (e.g. only between 7:00 and 14:00), call ahead, dock, liftgate..."
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
              />
            </div>
          )}
          {!deliveryOpen && (deliveryContact.name || deliveryInstructions) ? (
            <div className="mt-1 text-xs text-gray-600">
              {[deliveryContact.name, deliveryContact.phone].filter(Boolean).join(", ")}
              {deliveryInstructions ? ` - ${deliveryInstructions}` : ""}
            </div>
          ) : null}
        </div>
        <div className="mt-4 rounded-xl border border-gray-300 p-3">
          <div className="text-xs font-medium text-gray-900 mb-2">
            Payment Method
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="payment-method"
                value="credit_card"
                checked={paymentMethod === "credit_card"}
                onChange={() => setPaymentMethod("credit_card")}
              />
              Credit Card
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="payment-method"
                value="wire_transfer"
                checked={paymentMethod === "wire_transfer"}
                onChange={() => setPaymentMethod("wire_transfer")}
              />
              Wire Transfer
            </label>
          </div>
          {paymentMethod === "wire_transfer" && (
            <div className="mt-2 text-xs text-gray-600">
              You can complete checkout now and then submit your wire transfer
              receipt to <strong>procurement@kono.ai</strong>.
            </div>
          )}
        </div>
        <button
          type="submit"
          className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={creatingOrder}
        >
          {creatingOrder
            ? "Placing order..."
            : paymentMethod === "wire_transfer"
              ? "Place Wire Transfer Order"
              : "Continue"}
        </button>
        <div className="text-right">{totalPriceElem()}</div>
        {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
      </form>
    );
  }

  if (creatingOrder) {
    return <>...</>;
  }

  if (
    orderId &&
    orderPaymentMethod === "credit_card" &&
    clientSecret &&
    !paid
  ) {
    return (
      <PaymentForm
        clientSecret={clientSecret}
        totalPrice={totalPrice}
        currency={displayCurrency}
        onPaymentSuccess={handlePaymentSuccess}
      />
    );
  }
  if (
    orderId &&
    (paid ||
      !totalPrice ||
      (orderPaymentMethod === "wire_transfer" && !clientSecret))
  ) {
    try {
      // This checkout form received a subset of all cart items (either all priced or all non-priced),
      // so we need to clear the local storage cart items that are not in this checkout form
      // to avoid showing them in the cart again.
      const currItemIds = new Set(cartItems.map((item) => item.product.id));
      const storedCart = parseStoredCart(localStorage.getItem("shoppingCart"));
      const newCartItems = storedCart.cartItems.filter(
        (item) => !currItemIds.has(item.product.id),
      );
      persistShoppingCart({ cartItems: newCartItems });
    } catch (e) {
      console.error("Failed to clear shopping cart from local storage:", e);
    }
    return (
      <div className="text-center">
        <p className="font-medium text-lg">
          Thank you for your {paid ? "purchase" : "inquiry"}!
        </p>
        <p className="mt-4">Your order ID is: {orderId}</p>
        <p className="mt-4">
          You will receive an email{" "}
          {paid
            ? "confirmation shortly."
            : orderPaymentMethod === "wire_transfer"
              ? "with wire transfer instructions shortly."
              : "regarding your inquiry shortly."}
        </p>
        {orderPaymentMethod === "wire_transfer" && (
          <p className="mt-2 text-sm text-gray-700">
            Please submit your wire transfer receipt to{" "}
            <strong>{wireTransferRecipientEmail}</strong>.
          </p>
        )}
        {orderPromotionConfirmationMessage && (
          <p className="mt-4 text-sm text-gray-700">
            {orderPromotionConfirmationMessage}
          </p>
        )}
        {!user && (
          <p className="mt-4 text-xs">
            <Link
              to={`/signup?email=${guestEmail}&name=${getUserDataValues().name}`}
              className="underline"
            >
              Create an account
            </Link>{" "}
            to keep track of your order.
          </p>
        )}
      </div>
    );
  }

  return <>Something went wrong. Please try again or contact support.</>;
};
